import { Button, Modal } from 'components/core';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { IoWarningOutline } from 'react-icons/io5';
import { useI18n } from 'contexts/i18n';
import { FC, useState } from 'react';
import { Styled, RewardPointsWarningModalProps } from '.';
import getConfig from 'next/config';
import { useRetriveCart } from 'reactQuery/queries';
import { useVerifyRewardPoints } from 'reactQuery/mutations';
import { useRouter } from 'next/router';

const RewardPointsWarningModal: FC<RewardPointsWarningModalProps> = ({
  selectedShippingAddress,
  onSave,
  onClose,
}) => {
  const router = useRouter();
  const { data: currentCart } = useRetriveCart();
  const i18n = useI18n();
  const verifyRewardPointsMutation = useVerifyRewardPoints();

  const isOrderContainsRedeemabledTypodonts = () => {
    const { publicRuntimeConfig } = getConfig();
    const typodontSKUs = publicRuntimeConfig.APAC_TYPODONT_SKUS.split(',');
    const lineItems = currentCart?.lineItems
      .filter(item => item.isRewardPointsRedeemed)
      .map(item => item.sku);
    const typodontLineitems = lineItems?.filter(value =>
      typodontSKUs.includes(value)
    );
    return typodontLineitems && typodontLineitems.length > 0;
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [rewardPoints, setRewardPoints] = useState({ points: 0, docName: '' });

  const saveAddressHandler = async () => {
    if (currentCart && selectedShippingAddress) {
      const response = await verifyRewardPointsMutation.mutateAsync({
        shipAddressId: selectedShippingAddress.id,
      });

      if (response.has_sufficient_points) {
        onSave();
        onClose();
      } else {
        setRewardPoints({
          points: response.points,
          docName: response.doc_name,
        });
        setCurrentStep(2);
      }
    }
  };

  const goBackToCart = () => {
    router.push('/cartview?showcart=1');
  };

  return (
    <Modal>
      {currentStep === 1 && (
        <>
          <Styled.ModalTitle>
            <IoInformationCircleOutline />
            <span>{i18n['Information']}</span>
          </Styled.ModalTitle>
          <Styled.ModalBody>
            <p>
              {i18n['After order is submitted and processed, there will be:']}
              <ul>
                <li>{i18n['No return of products under redemption​.']}</li>
                <li>{i18n['No refund of Rewards points and cash portion.']}</li>
                <li>
                  {
                    i18n[
                      'No replacement of product, unless defective. Conditions apply.'
                    ]
                  }
                </li>
                {isOrderContainsRedeemabledTypodonts() && (
                  <li>
                    {i18n['Redeemed Typodont is for educational purpose only.']}
                  </li>
                )}
              </ul>
            </p>
          </Styled.ModalBody>
          <Styled.ModalFooter>
            <div>
              {verifyRewardPointsMutation.isLoading &&
                i18n['Verifying Reward Points...']}
            </div>
            <div>
              <Button variant="secondary" onClick={onClose}>
                {i18n['Cancel']}
              </Button>
              <Button
                onClick={saveAddressHandler}
                loading={verifyRewardPointsMutation.isLoading}
              >
                {i18n['Okay']}
              </Button>
            </div>
          </Styled.ModalFooter>
        </>
      )}
      {currentStep === 2 && (
        <>
          <Styled.ModalTitle>
            <IoWarningOutline />
            <span>{i18n['Warning']}</span>
          </Styled.ModalTitle>
          <Styled.ModalBody>
            {i18n[
              'You do not have enough reward points. The current available points are:'
            ]
              .replace('{docName}', rewardPoints.docName)
              .replace('{points}', rewardPoints.points.toString())}{' '}
          </Styled.ModalBody>
          <Styled.ModalFooter>
            <Button onClick={goBackToCart}>{i18n['Back To Cart']}</Button>
          </Styled.ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default RewardPointsWarningModal;
